/// <reference path="./global.d.ts" />
import { fitOptionToSizeMode } from '@creative/serialization/image-settings-serializer';
import { CreativeMode } from '@domain/creative/environment';
import { DEFAULT_IMAGE_QUALITY, ImageSizeMode } from '@domain/image';
import { preciseIOSVersion } from '../../../utils/src/lib/ad/browser';
import { alignWithinBounds, aspectRatioScale, boundsToPointRect, roundBounds } from '../../../utils/src/lib/geom';
import { sanitizeUrl } from '../../../utils/src/lib/sanitizer';
import { getFileExtension, getUrlParameters, isBase64Image, isRelativeUrl, isSameDomain, objectToQueryString, replaceOrigin } from '../../../utils/src/lib/url';
import { clamp, omitUndefined, roundToNearestMultiple } from '../../../utils/src/lib/utils';
export class ImageOptimizerUrlBuilder {
    static get origin() {
        var _a;
        return (_a = ImageOptimizerUrlBuilder._options) === null || _a === void 0 ? void 0 : _a.origin;
    }
    /**
     * @summary
     * Initialize ImageOptimizer class to build image urls
     */
    static async init_m(origin = '', defaultOrigin = '') {
        // WebP loading is broken in iOS >= 14.3 < 14.5.0
        let webP = false;
        const minorIOSVersion = preciseIOSVersion.simple;
        if (minorIOSVersion > 0 && minorIOSVersion >= 14.3 && minorIOSVersion < 14.5) {
            webP = false;
        }
        else {
            webP = await testWebP();
        }
        ImageOptimizerUrlBuilder._options = {
            webP,
            origin,
            defaultOrigin
        };
        return webP;
    }
    static getCroppingOptions(element) {
        const imageSettings = element.imageSettings;
        if (imageSettings.sizeMode === ImageSizeMode.Crop) {
            return getCroppingOptions(element, element.imageAsset, imageSettings.x, imageSettings.y);
        }
    }
    static getUrlByElement_m(element, settings = {}) {
        const { imageAsset, imageSettings } = element;
        const size = getMaxScaledSize(element, settings.sizeLimit);
        const url = settings.feedUrl || (imageAsset === null || imageAsset === void 0 ? void 0 : imageAsset.url) || '';
        const defaultQuality = settings.force ? 100 : undefined;
        const quality = typeof imageSettings.quality === 'number' ? imageSettings.quality : defaultQuality;
        let sizeMode = imageSettings.sizeMode;
        const cropping = settings.cropping;
        // Feeded images should always keep ratio
        if (!imageAsset) {
            sizeMode = sizeMode === ImageSizeMode.Crop ? ImageSizeMode.Crop : ImageSizeMode.Fit;
        }
        const { origin, defaultOrigin, webP } = ImageOptimizerUrlBuilder._options;
        const format = getFormat(url, webP);
        // Optimization turned off, just validate url
        if (format === 'svg' || format === 'gif' || typeof quality === 'undefined') {
            const urlWithOrigin = origin &&
                defaultOrigin &&
                isSameDomain(url, defaultOrigin) &&
                window.ENVIRONMENT !== 'local'
                ? replaceOrigin(url, origin)
                : url;
            return sanitizeUrl(urlWithOrigin);
        }
        // Return retina image if optimize is off in DV
        const dpr = ImageOptimizerUrlBuilder.getDevicePixelRatio(size.width, size.height, settings.env, typeof imageSettings.quality === 'number' ? imageSettings.highDpi : true);
        return ImageOptimizerUrlBuilder.getUrl(url, Object.assign(Object.assign({}, size), { sizeMode, quality, cropping, dpr }));
    }
    /**
     * Get url to the image optimizer.
     * WARNING: adding _m suffix to the method name will crash widgets using this
     * @param url
     * @param options
     * @returns
     */
    static getUrl(url, options) {
        const baseOptions = ImageOptimizerUrlBuilder._options;
        if (!baseOptions) {
            throw new Error('ImageOptimizerUrlBuilder not initiated');
        }
        return getImageOptimizerUrl(url, Object.assign({ webpSupport: baseOptions.webP, imageOptimizerOrigin: baseOptions.origin }, omitUndefined(options)));
    }
    /**
     * Check if an url or element is a cropped image. It might be needed
     * to detect if an image can be expected to have the same ratio as original image or not.
     * Note that images might already be cropped without affecting url when creative is used
     * as an "html-export"
     * @param urlOrElement
     * @returns
     */
    static isCropped_m(urlOrElement = '') {
        const url = typeof urlOrElement === 'string' ? urlOrElement : urlOrElement.imageUrl || '';
        // In html-export image optimizer will not be available
        if (!this.origin && typeof urlOrElement !== 'string') {
            // Svg and gifs are never cropped (gifs due to problems with IO)
            const format = getImageExtension(url);
            const isNotSvgOrGif = format !== 'svg' && format !== 'gif';
            const isSizeModeCrop = urlOrElement.__data.imageSettings.sizeMode === ImageSizeMode.Crop;
            // HTML-exported ads will have relative url's and should already be cropped
            return isRelativeUrl(url) && isSizeModeCrop && isNotSvgOrGif && !isBase64Image(url);
        }
        return !!url && ImageOptimizerUrlBuilder._isOptimized(url) && !!getUrlParameters(url)['x1'];
    }
    /**
     * Get original url from optimized or non optimized string
     */
    static getOriginalUrl(url = '') {
        if (ImageOptimizerUrlBuilder._isOptimized(url)) {
            return getOriginalUrl(url) || '';
        }
        return url;
    }
    /**
     * Check if url goes through image optimizer to request an optimized url;
     * @param url
     * @returns
     */
    static _isOptimized(url) {
        const origin = ImageOptimizerUrlBuilder.origin;
        return !!origin && isOptimizedUrl(url, origin);
    }
    static getDevicePixelRatio(width, height, env, highDpi) {
        // Note that this is affected by browser zoom, it can be like 1.6012
        let dpr = window.devicePixelRatio;
        if (env === CreativeMode.ImageGenerator || env === CreativeMode.VideoGenerator) {
            // STUDIO-6228 - use better quality images in exported creatives
            dpr = 2;
            // if the image is bigger remove the dpr scaling to prevent heavy load on the browser
            if (width * height >= 1000 * 1000) {
                dpr = 1;
            }
        }
        if (highDpi && dpr) {
            // Decrease load on IO by only having a few different variants of the same image (1x, 1.5x and 2x)
            return clamp(roundToNearestMultiple(dpr, 0.5), 1, 2);
        }
        return dpr;
    }
}
export function getImageOptimizerUrl(url, options) {
    if (!url) {
        throw new Error('No image url provided');
    }
    // Crash if it's not a friendly url
    sanitizeUrl(url);
    // Apply default values
    const mergedOptions = Object.assign(Object.assign({}, imageOptimizerDefaults), omitUndefined(options));
    const imageOptimizerOrigin = mergedOptions.imageOptimizerOrigin;
    // No URL to optimizer specified or relative (incl. base64) => Use original image
    if (!sanitizeUrl(imageOptimizerOrigin) || isRelativeUrl(url)) {
        return url;
    }
    const format = getFormat(url, mergedOptions.webpSupport);
    // Don't optimize SVG (or when wepb is not supported ignore gifs as well)
    if (format === 'svg' || format === 'gif') {
        return url;
    }
    // Support if an already optimized url is passed in by taking the ?u=encodedurl part as url
    if (url.indexOf(imageOptimizerOrigin) > -1) {
        const param = getUrlParameters(url)['u'];
        if (typeof param === 'string') {
            url = decodeURIComponent(param);
        }
    }
    if (!mergedOptions.width || !mergedOptions.height) {
        throw new Error('No size provided');
    }
    const position = mergedOptions.position
        ? { positionX: mergedOptions.position.x, positionY: mergedOptions.position.y }
        : undefined;
    const w = Math.round(mergedOptions.width * mergedOptions.dpr);
    const h = Math.round(mergedOptions.height * mergedOptions.dpr);
    // IO Doesn't crop images with quality 100 so make quality range from 0-99 temporarily
    const q = typeof mergedOptions.quality === 'number' ? clamp(mergedOptions.quality, 0, 99) : undefined;
    // WARNING: DO NOT CHANGE THE ORDER, DOING SO WILL INVALIDATE CACHING
    const imageOptimizerParameters = Object.assign(Object.assign({ u: encodeURIComponent(url), w,
        h,
        q, f: format, rt: getOptimizerRatioParameter(mergedOptions.sizeMode || mergedOptions.fitOption) }, position), mergedOptions.cropping);
    const query = objectToQueryString(imageOptimizerParameters);
    return `${imageOptimizerOrigin}/api/image/${mergedOptions.method}${query}`;
}
export function getCroppingOptions(size, originalSize, alignH = 0.5, alignV = 0.5) {
    if (!(originalSize === null || originalSize === void 0 ? void 0 : originalSize.width) || !originalSize.height) {
        return;
    }
    // Image size on canvas including the hidden/overflow areas
    const renderedImageSize = Object.assign(Object.assign({}, aspectRatioScale(originalSize, size, 'cover')), { x: 0, y: 0 });
    // Area within renderedImageSize to crop
    const croppingArea = alignWithinBounds(size, renderedImageSize, alignH, alignV);
    const scale = originalSize.width / renderedImageSize.width;
    // In IO crop happens BEFORE resizing so use positions relative to original size
    const crop = roundBounds({
        x: croppingArea.x * scale,
        y: croppingArea.y * scale,
        width: croppingArea.width * scale,
        height: croppingArea.height * scale
    });
    const points = boundsToPointRect(crop);
    // Never overflow original image (will cause a 400-error from IO)
    points.x2 = Math.min(points.x2, originalSize.width);
    points.y2 = Math.min(points.y2, originalSize.height);
    return points;
}
let webPPromise;
export function testWebP() {
    if (!webPPromise) {
        webPPromise = new Promise(res => {
            const webP = new Image();
            webP.onload = webP.onerror = () => res(webP.height === 2);
            webP.src =
                'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
        });
    }
    return webPPromise;
}
export function getFormat(url, webPSupport = false) {
    const originalFormat = getImageExtension(url);
    /**
     * Certain gifs does not render properly on iOS if it's
     * rendered as an animated webp
     */
    const iOSVersion = preciseIOSVersion.simple;
    if (originalFormat === 'gif' && iOSVersion > 0 && iOSVersion >= 14 && iOSVersion < 16) {
        webPSupport = false;
    }
    // SVG can't be converted to webp
    if (originalFormat === 'svg') {
        return originalFormat;
    }
    // WebP not supported
    else if (!webPSupport) {
        return !originalFormat || originalFormat === 'webp' ? 'png' : originalFormat;
    }
    return 'webp';
}
function getOptimizerRatioParameter(fitOption) {
    const sizeMode = fitOptionToSizeMode(fitOption);
    // Distort requires the image optimizer to contain the image
    if (sizeMode === ImageSizeMode.Stretch) {
        return 'contain';
    }
    if (sizeMode === ImageSizeMode.Fit) {
        return 'contain';
    }
    if (sizeMode === ImageSizeMode.Crop) {
        return 'cover';
    }
}
function getMaxScaledSize(element, sizeLimit) {
    const imageAsset = element.imageAsset;
    let scale = element.states.reduce((accumulator, state) => {
        const scaleX = state.scaleX || 0;
        const scaleY = state.scaleY || 0;
        return Math.max(scaleX, scaleY, accumulator);
    }, 1);
    if (sizeLimit) {
        // Cap size increase so that image never is larger than limit (with fit 'cover')
        if (element.width <= sizeLimit.width || element.height <= sizeLimit.height) {
            const coverSize = aspectRatioScale(element, sizeLimit, 'cover');
            scale = Math.min(coverSize.width / element.width, scale);
        }
        // Element is overflowing size limit, don't increase size
        else {
            scale = 1;
        }
    }
    // Don't allow image getting larger than original
    if ((imageAsset === null || imageAsset === void 0 ? void 0 : imageAsset.width) && (imageAsset === null || imageAsset === void 0 ? void 0 : imageAsset.height)) {
        const assetWidth = imageAsset.width;
        const assetHeight = imageAsset.height;
        if (assetWidth < element.width * scale) {
            return {
                width: assetWidth,
                height: assetHeight
            };
        }
    }
    return {
        width: element.width * scale,
        height: element.height * scale
    };
}
export function getImageExtension(url) {
    const format = getFileExtension(url);
    switch (format) {
        case 'jpg':
            return 'jpeg';
        case 'webp':
        case 'jpeg':
        case 'gif':
        case 'png':
        case 'svg':
            return format;
    }
}
/**
 * Get url parameter from a Image Optimiser URL
 * @param url
 * @returns
 */
export function getOriginalUrl(url) {
    return getUrlParameters(url)['u'];
}
export function isOptimizedUrl(url, imageOptimizerOrigin) {
    return url.indexOf(imageOptimizerOrigin) > -1 && !!getOriginalUrl(url);
}
export const imageOptimizerDefaults = {
    webpSupport: false,
    imageOptimizerOrigin: '',
    method: 'optimize',
    quality: DEFAULT_IMAGE_QUALITY,
    dpr: 1,
    env: CreativeMode.AnimatedCreative
};
